import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DialogV2, Flex, JoyKitThemeProvider } from '@withjoy/joykit';
import { FiltersPanel } from './components/FiltersPanel/FiltersPanel';
import { ListingsPanel } from './components/ListingsPanel/ListingsPanel';
import { Map } from './components/Map/Map';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { PICKED_BY_COUPLE_KEY, useListingFilters } from './hooks/useListingFilters';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { ChangedLocation, MapProps } from './components/Map/Map.types';
import { AccommodationsConsoleState, ListingWithPricing } from './AccommodationsConsole.types';
import { useAccommodationsConsoleListings } from './hooks/useAccommodationsConsoleListings';
import { useQueryParamHelper } from '@shared/core/queryString';
import { AccommodationsConsoleHeader } from './components/Header';
import { useAccommodationHeaderHandlers } from './hooks/useAccommodationHeaderHandlers';
import { EmptyState } from './components/EmptyState';
import { differenceInCalendarDays } from 'date-fns';
import { useBestAnchorLocation } from './hooks/useBestAnchorLocation';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

import { usePagination } from './hooks/usePagination';
import { ShowMoreButton } from './components/ListingsPanel';
import { MobileListingsDrawer } from './components/MobileListingsDrawer/MobileListingsDrawer';
import { MobileSearchComponent } from './components/MobileSearchBar';
import { BottomSheet } from './components/BottomSheet';
import { useGoogleMaps } from '@shared/hooks/useGoogleMaps';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useMobileSheetDisclosures } from './hooks/useMobileSheetDisclosures';
import { calculateStayDates } from '@shared/utils/dateHelpers';
import { useEventInfo } from '@shared/utils/eventInfo';

// this will likely become a provider
export const AccommodationsConsole = ({ eventName, onClose }: { eventName: string; onClose?: () => void }) => {
  const updateSearchParam = useUpdateSearchParam();
  const queryParamHelper = useQueryParamHelper();
  const { isFilterPanelOpen, onFilterPanelOpen, onFilterPanelClose, isSearchOpen, onSearchOpen, onSearchClose } = useMobileSheetDisclosures();
  const isMobileOrTablet = useIsMobileOrTablet();
  const { eventInfo } = useEventInfo();
  const { checkIn: eventStartDate, checkOut: eventEndDate } = calculateStayDates(eventInfo?.finalizedDate?.dateString);
  const [hidePanel, togglePanel] = useState(false);
  const [mapViewPort, setMapViewPort] = useState<{ center: google.maps.LatLngLiteral; bounds: google.maps.LatLngBounds }>();
  const [state, setState] = useState<AccommodationsConsoleState>(() => {
    const datesFromUrl = {
      start: queryParamHelper.getValueString('checkinDate'),
      end: queryParamHelper.getValueString('checkoutDate')
    };
    return {
      circularRegionSearch: {
        // Read location params from URL
        latitude: parseFloat(queryParamHelper.getValueString('lat') || '0'),
        longitude: parseFloat(queryParamHelper.getValueString('lng') || '0'),
        radius: 4
      },
      searchLocation: queryParamHelper.getValueString('searchLocation') || '',
      searchDates: {
        // TODO: Need to handle date parsing more robustly
        // 1. get from event date
        // 2. if one end is missing, derive the other date
        start: datesFromUrl.start || eventStartDate.dateString,
        end: datesFromUrl.end || eventEndDate.dateString
      },
      occupancies: {
        unitCount: 1,
        occupancies: [
          {
            adults: 2,
            childrenAges: []
          }
        ]
      }
    };
  });
  const [reCenterMap, setReCenterMap] = useState<MapProps['reCenterMap']>({ location: false, markers: false });

  const updateState = useEventCallback((partialState: Partial<AccommodationsConsoleState>) => {
    setState(prev => ({
      ...prev,
      ...partialState
    }));
  });

  const isCouplePick = useMemo(() => queryParamHelper.getValueString(PICKED_BY_COUPLE_KEY) === 'true', [queryParamHelper]);

  const onBestAnchorLocationCompleted = useEventCallback((data: { latitude: string; longitude: string; friendlyName: string } | null) => {
    if (data?.latitude && data?.longitude) {
      updateState({ circularRegionSearch: { latitude: +data.latitude, longitude: +data.longitude, radius: 4 }, searchLocation: data.friendlyName });
      setReCenterMap(prev => ({ ...prev, location: true }));
    }
  });

  const { loading: bestAnchorLocationLoading } = useBestAnchorLocation({
    onCompleted: onBestAnchorLocationCompleted,
    skip: !!(state.circularRegionSearch.latitude && state.circularRegionSearch.longitude)
  });

  const { cachedListings, listings, isFetchingListings, isFetchingListingPrices, pricingError } = useAccommodationsConsoleListings({
    state,
    variant: isCouplePick ? 'couple' : 'regional'
  });

  const { filters, filteredProperties, updateFilters, supportedFacilities, facilityCounts, starRatingCounts, guestRatingCounts, resetFilters } = useListingFilters(
    listings,
    isFetchingListingPrices
  );

  const headerHandlers = useAccommodationHeaderHandlers(updateState, () => setReCenterMap(prev => ({ ...prev, markers: true })));

  // Filter hotels as per map visible port
  const [googleMaps] = useGoogleMaps();
  const listingsInViewport = useMemo(() => {
    if (!mapViewPort || !googleMaps) return filteredProperties;

    const { center, bounds } = mapViewPort;
    const centerLatLng = new googleMaps.LatLng(center.lat, center.lng);
    const duplicateLocations = new Set<string>();
    return filteredProperties
      .flatMap(hotel => {
        const { latitude, longitude } = hotel.location || {};
        if (latitude == null || longitude == null) return [];

        if (duplicateLocations.has(hotel.joyPlaceId)) return [];

        const position = new googleMaps.LatLng(latitude, longitude);
        if (!bounds.contains(position)) return [];

        duplicateLocations.add(hotel.joyPlaceId);

        const distance = googleMaps.geometry.spherical.computeDistanceBetween(centerLatLng, position);
        return [{ hotel, distance }];
      })
      .sort((a, b) => a.distance - b.distance)
      .map(item => item.hotel);
  }, [filteredProperties, mapViewPort, googleMaps]);

  const mapMarkers = useMemo(() => {
    if (isCouplePick) {
      return filteredProperties.map(hotel => ({ lat: hotel.location.latitude, lng: hotel.location.longitude })).filter(Boolean);
    }
    return [];
  }, [filteredProperties, isCouplePick]);

  useEffect(() => {
    if (isCouplePick) {
      setReCenterMap(prev => ({ ...prev, markers: true }));
    } else {
      setReCenterMap(prev => ({ ...prev, location: true }));
    }
  }, [isCouplePick, queryParamHelper]);

  const onChangeLocation = useEventCallback((changedLocation: ChangedLocation) => {
    updateSearchParam(PICKED_BY_COUPLE_KEY, null, true);
    updateState({
      circularRegionSearch: { latitude: changedLocation.location.lat, longitude: changedLocation.location.lng, radius: changedLocation.radius }
    });
  });

  const onChangeMapViewPort = useEventCallback((center: { lat: number; lng: number }, bounds: google.maps.LatLngBounds) => {
    setMapViewPort({ center, bounds });
  });

  const onClickMarker = useEventCallback((hotel: ListingWithPricing) => {
    alert(`${hotel.name} - Hightlight in list`);
  });

  const handleOnTogglePanel = useEventCallback(() => {
    togglePanel(isOpen => !isOpen);
  });

  const handleResetFilters = useEventCallback(() => {
    resetFilters();
    setReCenterMap(prev => ({ ...prev, location: true }));
  });

  const handlePickedByCoupleToggle = useEventCallback(() => {
    updateFilters({ ...filters, pickedByCouple: !filters.pickedByCouple });
  });

  const noOfNights = useMemo(() => differenceInCalendarDays(new Date(state.searchDates.end), new Date(state.searchDates.start)), [state.searchDates]);

  const { paginatedListings, showNextPageOfListings, hasMoreListingsToShow, listingsContainerRef } = usePagination({ listings: listingsInViewport, filters });

  const renderListingsPanel = useCallback(() => {
    return (
      <SkeletonGroup
        isReady={!isFetchingListings && !bestAnchorLocationLoading}
        overflowY={{ _: 'auto', md: 'scroll' }}
        width={{ _: '100%', md: hidePanel ? 0 : '50%' }}
        paddingBottom={9}
        placeholder={<ListingsPanel.Skeleton isMobileOrTablet={isMobileOrTablet} />}
        rootRef={listingsContainerRef}
      >
        {paginatedListings.length > 0 ? (
          <>
            <ListingsPanel
              occupancies={state.occupancies}
              filters={filters}
              onFiltersChange={updateFilters}
              noOfNights={noOfNights}
              venueCoordinates={state.circularRegionSearch}
              listings={paginatedListings}
              isLoadingPrices={isFetchingListingPrices}
              hidePanel={hidePanel}
            />
            {hasMoreListingsToShow && <ShowMoreButton onClick={showNextPageOfListings} />}
          </>
        ) : (
          <EmptyState onReset={handleResetFilters} showResetButton={!!cachedListings?.length} />
        )}
      </SkeletonGroup>
    );
  }, [
    isFetchingListings,
    bestAnchorLocationLoading,
    hidePanel,
    isMobileOrTablet,
    listingsContainerRef,
    paginatedListings,
    state.occupancies,
    state.circularRegionSearch,
    filters,
    updateFilters,
    noOfNights,
    isFetchingListingPrices,
    hasMoreListingsToShow,
    showNextPageOfListings,
    handleResetFilters,
    cachedListings?.length
  ]);

  const renderFiltersPanel = useCallback(() => {
    return (
      <SkeletonGroup isReady={!isFetchingListings && !bestAnchorLocationLoading} placeholder={<FiltersPanel.Skeleton />}>
        <FiltersPanel
          filters={filters}
          eventName={eventName}
          onFiltersChange={updateFilters}
          supportedFacilities={supportedFacilities}
          facilityCounts={facilityCounts}
          starRatingCounts={starRatingCounts}
          prices={listings.map(listing => listing.pricingInfo?.nightlyBaseRate).filter(Boolean) as number[]}
          guestRatingCounts={guestRatingCounts}
          isLoadingPrices={isFetchingListingPrices}
          hidePriceRangeFacet={!!pricingError}
          onClose={onFilterPanelClose}
        />
      </SkeletonGroup>
    );
  }, [
    eventName,
    isFetchingListings,
    bestAnchorLocationLoading,
    filters,
    updateFilters,
    supportedFacilities,
    facilityCounts,
    starRatingCounts,
    listings,
    guestRatingCounts,
    isFetchingListingPrices,
    pricingError,
    onFilterPanelClose
  ]);

  return (
    <Flex flexDirection="column" width={1480} height={'100%'} maxWidth="100%" overflow="auto" borderRadius="inherit">
      <AccommodationsConsoleHeader
        handleSearchSubmit={headerHandlers.handleHeaderSearchSubmit}
        handleFormSubmit={headerHandlers.handleHeaderFormSubmit}
        state={state}
        onFilterPanelOpen={onFilterPanelOpen}
        onSearchOpen={onSearchOpen}
        onClose={onClose}
        handlePickedByCoupleToggle={handlePickedByCoupleToggle}
        isPickedByCouple={filters.pickedByCouple}
      />

      {/* Main Content */}
      <Flex height={{ _: '100%', md: 760 }} position="relative">
        {!isMobileOrTablet && (
          <>
            {renderFiltersPanel()}
            {renderListingsPanel()}
          </>
        )}
        {state.circularRegionSearch.latitude && state.circularRegionSearch.longitude ? (
          <Map
            location={{ latlng: { lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude } }}
            hotels={paginatedListings}
            isFetchingHotels={isFetchingListings}
            onChangeLocation={onChangeLocation}
            onChangeMapViewPort={onChangeMapViewPort}
            onClickMarker={onClickMarker}
            onToggleLeftPanel={handleOnTogglePanel}
            setReCenterMap={setReCenterMap}
            reCenterMap={reCenterMap}
            hidePanel={hidePanel}
            mapMarkers={mapMarkers as { lat: number; lng: number }[]}
          />
        ) : null}
      </Flex>
      {isMobileOrTablet && (
        <>
          <MobileListingsDrawer>{renderListingsPanel()}</MobileListingsDrawer>
          <BottomSheet isOpen={isFilterPanelOpen} onClose={onFilterPanelClose}>
            {renderFiltersPanel()}
          </BottomSheet>
          <BottomSheet isOpen={isSearchOpen} onClose={onSearchClose}>
            <MobileSearchComponent
              onClose={onSearchClose}
              state={state}
              handleSearchSubmit={headerHandlers.handleHeaderSearchSubmit}
              handleFormSubmit={headerHandlers.handleHeaderFormSubmit}
            />
          </BottomSheet>
        </>
      )}
    </Flex>
  );
};

export const AccommodationsConsoleDialog = ({ isOpen, onClose, eventName }: { isOpen: boolean; onClose: () => void; eventName: string }) => {
  return (
    <>
      <DialogV2
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior="inside"
        disableFocusLockRemoveScroll
        overrides={{
          Content: {
            props: {
              width: 'auto',
              height: '100%',
              overflow: 'hidden'
            }
          },
          ContentContainer: {
            props: {
              width: 'auto'
            }
          }
        }}
      >
        <JoyKitThemeProvider>
          <AccommodationsConsole eventName={eventName} onClose={onClose} />
        </JoyKitThemeProvider>
      </DialogV2>
    </>
  );
};
